import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ClockIcon,
  MapIcon,
  CreditCardIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  path: string;
  color: string;
  bgColor: string;
}

const quickActions: QuickAction[] = [
  {
    id: 'new-conversion',
    title: 'Start New Conversion',
    description: 'Convert payroll files to V3 format',
    icon: PlusCircleIcon,
    path: '/converter',
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
  },
  {
    id: 'view-history',
    title: 'View History',
    description: 'See all past conversions',
    icon: ClockIcon,
    path: '/history',
    color: 'text-secondary-600',
    bgColor: 'bg-secondary-50',
  },
  {
    id: 'manage-mappings',
    title: 'Manage Mappings',
    description: 'Configure field mappings',
    icon: MapIcon,
    path: '/mappings',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  {
    id: 'subscription',
    title: 'View Subscription',
    description: 'Manage your plan and billing',
    icon: CreditCardIcon,
    path: '/subscription',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
];

interface QuickActionsProps {
  loading?: boolean;
}

export default function QuickActions({ loading = false }: QuickActionsProps) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="bg-white overflow-hidden shadow rounded-lg animate-pulse"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 bg-gray-200 rounded-md"></div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-32"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {quickActions.map((action, index) => {
        const Icon = action.icon;
        return (
          <motion.div
            key={action.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <button
              onClick={() => navigate(action.path)}
              className="w-full bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-all duration-200 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div
                      className={`flex items-center justify-center h-12 w-12 rounded-md ${action.bgColor}`}
                    >
                      <Icon
                        className={`h-6 w-6 ${action.color}`}
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {action.title}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {action.description}
                    </p>
                  </div>
                </div>
              </div>
            </button>
          </motion.div>
        );
      })}
    </div>
  );
}
